import type {
  CornerDotType,
  CornerSquareType,
  DotType,
  ErrorCorrectionLevel,
  QrCustomization,
} from '../api/types';

interface Props {
  value: QrCustomization;
  onChange: (next: QrCustomization) => void;
  hasLogo: boolean;
  logoSlot?: React.ReactNode;
}

const DOT_TYPES: { id: DotType; label: string }[] = [
  { id: 'square', label: 'Square' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'dots', label: 'Dots' },
  { id: 'classy', label: 'Classy' },
  { id: 'classy-rounded', label: 'Classy-rounded' },
  { id: 'extra-rounded', label: 'Extra-rounded' },
];

const CORNER_SQUARE_TYPES: { id: CornerSquareType; label: string }[] = [
  { id: 'square', label: 'Square' },
  { id: 'extra-rounded', label: 'Rounded' },
  { id: 'dot', label: 'Dot' },
];

const CORNER_DOT_TYPES: { id: CornerDotType; label: string }[] = [
  { id: 'square', label: 'Square' },
  { id: 'dot', label: 'Dot' },
];

const EC_LEVELS: { id: ErrorCorrectionLevel; label: string; tip: string }[] = [
  {
    id: 'L',
    label: 'L · 7%',
    tip:
      'Low (7% recovery). Smallest, sparsest pattern. Up to 7% of the QR can be damaged or obscured before it stops scanning. Use only for clean indoor prints without a logo.',
  },
  {
    id: 'M',
    label: 'M · 15%',
    tip:
      'Medium (15% recovery). Default for plain printed QRs. Up to 15% of the QR can be damaged or obscured. Good middle-ground for most printed materials with no logo.',
  },
  {
    id: 'Q',
    label: 'Q · 25%',
    tip:
      'Quartile (25% recovery). Better for outdoor placements, scuffed surfaces, table tents that may get food/drink on them. Up to 25% of the QR can be damaged or obscured.',
  },
  {
    id: 'H',
    label: 'H · 30%',
    tip:
      'High (30% recovery). Densest pattern, most resilient. Required when adding a center logo — surrounding dots reconstruct what is hidden behind the logo. Up to 30% of the QR can be damaged or obscured.',
  },
];

function chip(active: boolean): string {
  return [
    'px-3 py-1.5 text-xs rounded-md border transition-colors whitespace-nowrap',
    active
      ? 'bg-violet-950/40 border-violet-500 text-violet-200'
      : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-200',
  ].join(' ');
}

export function QrCustomizationPanel({ value, onChange, hasLogo, logoSlot }: Props) {
  const setDots = (type: DotType) =>
    onChange({ ...value, dotsOptions: { ...value.dotsOptions, type } });
  const setCornerSquare = (type: CornerSquareType) =>
    onChange({ ...value, cornersSquareOptions: { ...value.cornersSquareOptions, type } });
  const setCornerDot = (type: CornerDotType) =>
    onChange({ ...value, cornersDotOptions: { ...value.cornersDotOptions, type } });
  const setEc = (errorCorrectionLevel: ErrorCorrectionLevel) =>
    onChange({ ...value, qrOptions: { errorCorrectionLevel } });

  const setForeground = (color: string) =>
    onChange({
      ...value,
      dotsOptions: { ...value.dotsOptions, color },
      cornersSquareOptions: { ...value.cornersSquareOptions, color },
      cornersDotOptions: { ...value.cornersDotOptions, color },
    });

  const setBackground = (color: string) =>
    onChange({ ...value, backgroundOptions: { color } });

  return (
    <div className="space-y-5">
      <FieldGroup label="Body / dot style">
        <div className="flex flex-wrap gap-1.5">
          {DOT_TYPES.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setDots(opt.id)}
              className={chip(value.dotsOptions.type === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Eye / corner style">
        <div className="flex gap-1.5">
          {CORNER_SQUARE_TYPES.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setCornerSquare(opt.id)}
              className={chip(value.cornersSquareOptions.type === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Eye dot style">
        <div className="flex gap-1.5">
          {CORNER_DOT_TYPES.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setCornerDot(opt.id)}
              className={chip(value.cornersDotOptions.type === opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Foreground color">
        <ColorInput value={value.dotsOptions.color} onChange={setForeground} />
      </FieldGroup>

      <FieldGroup label="Background color">
        <ColorInput value={value.backgroundOptions.color} onChange={setBackground} />
      </FieldGroup>

      {logoSlot && <FieldGroup label="Center logo">{logoSlot}</FieldGroup>}

      <FieldGroup
        label="Error correction"
        hint={
          hasLogo
            ? 'Logo present — H (30%) recommended for best scannability.'
            : 'M (15%) is fine for plain QRs. Bump higher if you add a logo.'
        }
      >
        <div className="flex flex-wrap gap-1.5">
          {EC_LEVELS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setEc(opt.id)}
              className={chip(value.qrOptions.errorCorrectionLevel === opt.id)}
              title={opt.tip}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FieldGroup>
    </div>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-gray-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function ColorInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-9 h-9 rounded border border-gray-700 bg-transparent cursor-pointer"
        aria-label="Pick color"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
        }}
        className="bg-gray-900 border border-gray-800 rounded px-2 py-1.5 text-xs text-gray-200 font-mono w-28 focus:outline-none focus:border-violet-600"
      />
    </div>
  );
}
